import { CurrentUser } from "@/components/Page";
import { CostToUnitData } from "@/pages/api/gpt3-cost-to-unit";
import { IngredientSearchData } from "@/pages/api/gpt3-ingredient-search";
import {
  AncillariesQuery,
  ClassViewGridQuery,
  IngredientQuery,
  OrderCarouselQuery,
  OrderSessionsQuery,
  Recipe_MadeInGroups,
} from "@/queries/autogenerate/graphql";
import {
  AcademicCapIcon,
  BeakerIcon,
  CubeIcon,
  FolderIcon,
  RectangleStackIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import Fuse from "fuse.js";
import { MatchSorterOptions, matchSorter } from "match-sorter";
import { HTMLAttributes } from "react";
import { compareTwoStrings } from "string-similarity";

export const sum = (arr: number[]) => arr.reduce((acc, cur) => acc + cur, 0);

export function sumBy<T>(arr: T[], fn: (n: T) => number) {
  return arr.reduce((acc, cur) => acc + fn(cur), 0);
}

// Compare function for sorting
export const compare = (a?: any, b?: any) => {
  if (!a) return -1;
  if (!b) return 1;

  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  // a must be equal to b
  return 0;
};

function splitProper(string: string) {
  const letters = string.split("");
  const result: (string | number)[] = [];
  let holding: number | null = null;

  for (const letter of letters) {
    if (!Number(letter)) {
      if (holding) {
        holding = Number(`${holding}${letter}`);
      } else {
        holding = Number(letter);
      }
    } else {
      if (holding) {
        result.push(holding);
        holding = null;
      }
      result.push(letter);
    }
  }

  if (holding) {
    result.push(holding);
  }

  return result;
}

export function compareProper(a: string, b: string) {
  const ap = splitProper(a);
  const bp = splitProper(b);

  const value = -1;

  for (let index = 0; index < ap.length; index++) {
    const la = ap[index];
    const lb = bp[index];

    if (!lb) {
      return 1;
      break;
    } else if (la !== lb) {
      return la > lb ? 1 : -1;
      break;
    }
  }

  return value;
}

export function compareAlphanumerically(a?: string, b?: string): 0 | 1 | -1 {
  if (!a) return -1;
  if (!b) return 1;

  const re = /(\d+(\.\d+)?)|(\D+)/g;
  const aParts = a.match(re);
  const bParts = b.match(re);

  for (
    let i = 0, l = Math.max(aParts?.length ?? 0, bParts?.length ?? 0);
    i < l;
    i++
  ) {
    const aPart = aParts?.[i];
    const bPart = bParts?.[i];

    if (!aPart) return -1;
    if (!bPart) return 1;

    if (aPart !== bPart) {
      const aIsNum = !isNaN(parseFloat(aPart));
      const bIsNum = !isNaN(parseFloat(bPart));
      if (aIsNum && bIsNum) {
        return parseFloat(aPart) - parseFloat(bPart) > 0 ? 1 : -1;
      } else if (aIsNum) {
        return 1;
      } else if (bIsNum) {
        return -1;
      } else {
        return compare(aPart, bPart);
      }
    }
  }

  return 0;
}

// helper sorting functions
export const sortAlphabetically = (
  a?: { name: string },
  b?: { name: string }
) =>
  compareAlphanumerically(
    a?.name.trimStart().toLowerCase(),
    b?.name.trimStart().toLowerCase()
  );
export const sortAlphabeticallyReversed = (
  a?: { name: string },
  b?: { name: string }
) =>
  compareAlphanumerically(
    b?.name.trimStart().toLowerCase(),
    a?.name.trimStart().toLowerCase()
  );
export const sortByOldest = (
  a: { createdAt: string },
  b: { createdAt: string }
) => compare(new Date(a?.createdAt), new Date(b?.createdAt));
export const sortByNewest = (
  a: { createdAt: string },
  b: { createdAt: string }
) => compare(new Date(b?.createdAt), new Date(a?.createdAt));
export const sortByLastUpdated = (
  a: { updatedAt: string },
  b: { updatedAt: string }
) => compare(new Date(b?.updatedAt), new Date(a?.updatedAt));
export const sortByFirstUpdated = (
  a: { updatedAt: string },
  b: { updatedAt: string }
) => compare(new Date(a?.updatedAt), new Date(b?.updatedAt));
export const sortByTag = (
  a: { name: string; tags: [{ name: string }] },
  b: { name: string; tags: [{ name: string }] }
) =>
  a.tags.map((t) => t?.name.trimStart().toLowerCase()).join("") ===
  b.tags.map((t) => t?.name.trimStart().toLowerCase()).join("")
    ? sortAlphabetically(a, b)
    : compareAlphanumerically(
        a.tags.map((t) => t?.name.trimStart().toLowerCase()).join(""),
        b.tags.map((t) => t?.name.trimStart().toLowerCase()).join("")
      );
export const sortByTagReversed = (
  a: { name: string; tags: [{ name: string }] },
  b: { name: string; tags: [{ name: string }] }
) =>
  b.tags.map((t) => t?.name.trimStart().toLowerCase()).join("") ===
  a.tags.map((t) => t?.name.trimStart().toLowerCase()).join("")
    ? sortAlphabeticallyReversed(a, b)
    : compareAlphanumerically(
        b.tags.map((t) => t?.name.trimStart().toLowerCase()).join(""),
        a.tags.map((t) => t?.name.trimStart().toLowerCase()).join("")
      );
export const sortByClass = (
  a: { classes: [{ name: string }] },
  b: { classes: [{ name: string }] }
) =>
  compareAlphanumerically(
    a.classes.map((t) => t?.name.trimStart().toLowerCase()).join(""),
    b.classes.map((t) => t?.name.trimStart().toLowerCase()).join("")
  );
export const sortByClassReversed = (
  a: { classes: [{ name: string }] },
  b: { classes: [{ name: string }] }
) =>
  compareAlphanumerically(
    b.classes.map((t) => t?.name.trimStart().toLowerCase()).join(""),
    a.classes.map((t) => t?.name.trimStart().toLowerCase()).join("")
  );

export function sortBy<T>(arr: T[], k: keyof T) {
  return arr.concat().sort((a, b) => (a[k] > b[k] ? 1 : a[k] < b[k] ? -1 : 0));
}

export function sortByFn<T>(arr: T[], fn: (item: T) => number) {
  return arr
    .concat()
    .sort((a, b) => (fn(a) > fn(b) ? 1 : fn(a) < fn(b) ? -1 : 0));
}

// lower case, upper case
export const lowerCase = (str: string) => str.toLowerCase();
export const upperCase = (str: string) => str.toUpperCase();

export const includesAllWords = (searchStr: string, str: string) =>
  str.split(" ").every((word) => searchStr.includes(word));

export function fuzzySearchMutipleWords<R>(
  rows: R[], // array of data [{a: "a", b: "b"}, {a: "c", b: "d"}]
  filterValue: string, // potentially multi-word search string "two words"
  options: MatchSorterOptions<R> // keys to search ["a", "b"]
) {
  if (!filterValue || !filterValue.length) {
    return rows;
  }

  const terms = filterValue.split(" ");
  if (!terms) {
    return rows;
  }

  // reduceRight will mean sorting is done by score for the _first_ entered word.
  return terms.reduceRight(
    (results, term) => matchSorter(results, term, options),
    rows
  ) as R[];
}

export const classToOption = (
  c: NonNullable<CurrentUser["school"]>["classes"][number]
) => {
  return {
    value: c.id,
    label: (
      <p>
        {c.name}
        {c.users.filter(isTeacher).map((t) => (
          <span key={t.id} className="tag">
            {t.name}
          </span>
        ))}
      </p>
    ),
  };
};

export type TagOptionType = {
  value: string;
  label: string;
  __isNew__: boolean;
  type: "tag";
};

// Search for tags
export const searchTags: (
  query: string,
  tags: AncillariesQuery["tags"],
  filterTags?: AncillariesQuery["tags"]
) => TagOptionType[] = (query = "", tags, filterTags = []) =>
  fuzzySearchMutipleWords(tags, query, {
    keys: ["name"],
  })
    .filter(
      (t: AncillariesQuery["tags"][number]) =>
        !filterTags.some((ft) => ft.id === t.id)
    )
    .map((t: AncillariesQuery["tags"][number]) => ({
      value: t.id,
      label: t.name,
      __isNew__: false,
      type: "tag",
    }));

// search for inventory items
type InventoryItem = {
  name: string;
  tags: { name: string }[];
};

export function searchInventory<T extends InventoryItem>(
  query: string = "",
  items: T[],
  options?: {
    accuracyThreshold?: number;
    take?: number;
  }
) {
  const fuse = new Fuse(items, {
    useTokenSearch: true,
    includeScore: true,
    threshold: 0.4,
    keys: [
      {
        name: "name",
        weight: 0.7,
      },
      {
        name: "tags.name",
        weight: 0.3,
      },
    ],
  });

  const results = fuse
    .search(query, { limit: options?.take ?? 30 })
    // .filter((result) => result.score && result.score <= 0.5)
    .map((result) => result.item);

  return query
    ? results.filter((item) => {
        const similarity = compareTwoStrings(
          item.name.toLowerCase(),
          query.toLowerCase()
        );
        return similarity > (options?.accuracyThreshold ?? 0);
      })
    : results;

  // return fuzzySearchMutipleWords(items, query, {
  //   keys: ["name", (item) => item.tags.map((t) => t.name)],
  // });

  // const results = items.sort(
  //   (a, b) =>
  //     compareTwoStrings(a.name.toLowerCase(), query.toLowerCase()) +
  //     compareTwoStrings(
  //       a.tags
  //         .map((t) => t.name)
  //         .join(" ")
  //         .toLowerCase(),
  //       query.toLowerCase()
  //     ) -
  //     (compareTwoStrings(b.name.toLowerCase(), query.toLowerCase()) +
  //       compareTwoStrings(
  //         b.tags
  //           .map((t) => t.name)
  //           .join(" ")
  //           .toLowerCase(),
  //         query.toLowerCase()
  //       ))
  // );

  // return (options?.take ? results.slice(0, options.take) : results).filter(
  //   (item) => {
  //     const similarity = compareTwoStrings(
  //       item.name.toLowerCase(),
  //       query.toLowerCase()
  //     );
  //     return similarity > (options?.accuracyThreshold ?? 0);
  //   }
  // );
}

// search for recipes
export const searchRecipes = (
  query: string = "",
  items: OrderCarouselQuery["recipes"]
) =>
  fuzzySearchMutipleWords(items, query, {
    keys: [
      "name",
      (item) => item.tags.map((t) => t.name),
      (item) => item.classes.map((t) => t.name),
      (item) => format(new Date(item.createdAt), "p EEEE LLLL do, yyyy"),
    ],
  });

// export const formatRecipeLabel = (recipe: {
//   name: string;
//   madeInGroups?: Recipe_MadeInGroups;
//   tags: {
//     id: string;
//     name: string;
//   }[];
//   classes: {
//     id: string;
//     name: string;
//   }[];
// }) => (
export const formatRecipeLabel = (
  recipe: Pick<
    OrderCarouselQuery["recipes"][number],
    "name" | "madeInGroups" | "classes" | "tags" | "recipeCollection"
  >
) => (
  <p>
    {recipe.name}
    {recipe.madeInGroups ? (
      <small className="mx-1.5 rounded bg-cyan-200 px-1.5 py-0.5 font-medium">
        {recipe.madeInGroups == Recipe_MadeInGroups.Individuals
          ? "Made Individually"
          : recipe.madeInGroups == Recipe_MadeInGroups.Pairs
          ? "Made in Pairs"
          : recipe.madeInGroups == Recipe_MadeInGroups.Trios
          ? "Made in Groups of Three"
          : recipe.madeInGroups == Recipe_MadeInGroups.Four
          ? "Made in Groups of Four"
          : recipe.madeInGroups == Recipe_MadeInGroups.Five
          ? "Made in Groups of Five"
          : recipe.madeInGroups == Recipe_MadeInGroups.Six
          ? "Made in Groups of Six"
          : ""}
      </small>
    ) : null}
    {recipe.classes.map((c) => (
      <span key={c.id} className="tag tag-cyan">
        <AcademicCapIcon className="h-4" />
        {c.name}
      </span>
    ))}
    {recipe.tags.map((t) => (
      <span key={t.id} className="tag">
        <TagIcon className="h-4" />
        {t.name}
      </span>
    ))}
    {recipe.recipeCollection ? (
      <span className="tag tag-blue">
        <FolderIcon className="h-4" />
        {recipe.recipeCollection.name}
      </span>
    ) : null}
  </p>
);

// shape into a value for a recipe to be ordered (confusing?)
export const shapeIntoOrderRecipeValue = (
  recipe: OrderCarouselQuery["recipes"][number]
) => ({
  value: recipe.id,
  label: formatRecipeLabel(recipe),
});

// search for recipes to order
export const searchRecipesToOrder = (
  query: string = "",
  recipes: {
    name: string;
    tags: {
      id: string;
      name: string;
    }[];
    classes: {
      id: string;
      name: string;
    }[];
    // recipeIngredients: { ingredient: { name: string } }[];
  }[]
) =>
  fuzzySearchMutipleWords(recipes, query, {
    keys: [
      "name",
      (item) => item.tags.map((t) => t.name),
      (item) => item.classes.map((t) => t.name),
      // (item: { recipeIngredients: { ingredient: { name: string } }[] }) =>
      //   item.recipeIngredients.map((i) => i.ingredient.name),
    ],
  }).map(shapeIntoOrderRecipeValue);
// .slice(0, query.length > 2 ? undefined : 100); // reduce # of results for faster loading

// search for recipes to order
export type RecipeOptionType = {
  value: OrderCarouselQuery["recipes"][number];
  label: ReturnType<typeof formatRecipeLabel>;
};
export const searchRecipesForLessons = (
  query: string = "",
  recipes: OrderCarouselQuery["recipes"]
): RecipeOptionType[] =>
  fuzzySearchMutipleWords<OrderCarouselQuery["recipes"][number]>(
    recipes,
    query,
    {
      keys: [
        "name",
        (item: {
          id: string;
          name: string;
          tags: { id: string; name: string }[];
          classes: { id: string; name: string }[];
        }) => item.tags.map((t) => t.name),
        // (item: { recipeIngredients: { ingredient: { name: string } }[] }) =>
        //   item.recipeIngredients.map((i) => i.ingredient.name),
      ],
    }
  ).map((recipe) => ({
    value: recipe,
    label: formatRecipeLabel(recipe),
  }));
// .slice(0, query.length > 2 ? undefined : 100); // reduce # of results for faster loading

// search for orders
export const searchOrders: (
  query: string,
  items: OrderSessionsQuery["orderSessions"][number]["orders"]
) => OrderSessionsQuery["orderSessions"][number]["orders"] = (query, items) =>
  fuzzySearchMutipleWords(items, query, {
    keys: ["ingredient.name", "ingredient.unit", "user.name", "classe.name"],
  });

// Add ellipses if text is too long
export const ellipsise = (text: string, maxLength: number = 20) =>
  text.length > maxLength ? `${text.substring(0, maxLength - 3)}...` : text;

// localStorage helpers
export const getLocalStorage = (key: string, defaultValue: any = null) =>
  JSON.parse(localStorage.getItem(key) || JSON.stringify(defaultValue));
export const setLocalStorage = (key: string, value: any) =>
  localStorage.setItem(key, JSON.stringify(value));

// check if a user has a given role
// (role: string) => (user: { roles: { id: string; name: string }[] }) =>
const hasRole =
  (role: string) => (user: Partial<Pick<CurrentUser, "roles">> | undefined) =>
    user?.roles?.some((r) => r.name == role) ?? false;

export const isAdmin = hasRole("admin");
export const isAssistant = hasRole("assistant");
export const isTeacher = hasRole("teacher");
export const isStudent = hasRole("student");

// remove item from graphql cache
// export const removeFromCache = (cache, query, key, id) => {
//   const c = cache.readQuery({ query });

//   cache.writeQuery({
//     query,
//     data: {
//       [key]: c[key].filter(i => i.id !== id)
//     }
//   });
// };

// A simple custom fetch wrapper
export async function fetch(url: string, customConfig: RequestInit = {}) {
  const config: RequestInit = {
    method: "GET",
    ...customConfig,
  };
  return await window
    .fetch(url, config)
    .then(async (response) => {
      if (response.ok) {
        return response;
      } else {
        return Promise.reject(response.text());
      }
    })
    .then(async (response) => {
      const data = await response.json();
      if (response.ok) {
        return data;
      } else {
        return Promise.reject(data);
      }
    });
}

// Debounce function from https://davidwalsh.name/javascript-debounce-function
// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
export function debounce(func: () => any, wait: number, immediate?: boolean) {
  let timeout: NodeJS.Timeout | undefined;
  return function () {
    const context = this,
      args = arguments;
    const later = function () {
      timeout = undefined;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

export type GroupedOrderedIngredients = {
  id: string;
  name: string;
  orderedIngredients: ClassViewGridQuery["orderedIngredients"];
}[];

export const getOrderSessionsFromOrderedIngredients = (
  orderedIngredients: ClassViewGridQuery["orderedIngredients"]
) => {
  type RawOrder =
    ClassViewGridQuery["orderedIngredients"][number]["orders"][number] & {
      ingredient: Pick<
        ClassViewGridQuery["orderedIngredients"][number],
        "id" | "name" | "units" | "density" | "measurement" | "prices" | "tags"
      >;
    };
  const rawOrders = orderedIngredients.reduce<RawOrder[]>(
    (acc, cur) => [
      ...acc,
      ...cur.orders.map((o) => ({
        ...o,
        ingredient: {
          id: cur.id,
          name: cur.name,
          units: cur.units,
          displayUnit: cur.displayUnit,
          shoppingDisplayUnit: cur.shoppingDisplayUnit,
          selectionUnit: cur.selectionUnit,
          density: cur.density,
          measurement: cur.measurement,
          prices: cur.prices,
          tags: cur.tags,
        },
      })),
    ],
    []
  );

  const rawOrderSessions = orderedIngredients.reduce<
    ClassViewGridQuery["orderedIngredients"][number]["orders"][number]["orderSession"][]
  >((acc, cur) => [...acc, ...cur.orders.map((o) => o.orderSession)], []);

  const uniqueOrderSessions = [
    ...new Set(rawOrderSessions.map((os) => os.id)),
  ].map((id) => rawOrderSessions.find((r) => r.id == id));

  return uniqueOrderSessions.map((os) => ({
    ...os,
    orders: orderedIngredients
      .map((oi) =>
        oi.orders
          .filter((o) => o.orderSession.id == os?.id)
          .map((o) => rawOrders.find((ro) => ro.id == o.id))
          .filter((os): os is NonNullable<typeof os> => !!os)
      )
      .filter((oi) => oi.length)
      .flat()
      .reduce<
        (ClassViewGridQuery["orderedIngredients"][0]["orders"][number] & {
          ingredient: Pick<
            ClassViewGridQuery["orderedIngredients"][0],
            | "id"
            | "name"
            | "units"
            | "displayUnit"
            | "shoppingDisplayUnit"
            | "selectionUnit"
            | "density"
            | "measurement"
            | "prices"
            | "tags"
          >;
        })[]
      >(
        (acc, cur) => (acc.find((o) => o.id === cur.id) ? acc : [...acc, cur]),
        []
      ),
    // orders: orderedIngredients.map(oi => ({
    //   ...oi,
    //   orders: oi.orders.filter(o => o.orderSession.id == os.id)
    // })).filter(oi => oi.orders.length)
  }));
};

export const getClassesFromOrderedIngredients: (
  orderedIngredients: ClassViewGridQuery["orderedIngredients"]
) => (ClassViewGridQuery["orderedIngredients"][number]["orders"][number]["classe"] &
  GroupedOrderedIngredients[0])[] = (orderedIngredients) => {
  const rawClasses = orderedIngredients.reduce<
    ClassViewGridQuery["orderedIngredients"][number]["orders"][number]["classe"][]
  >((acc, cur) => [...acc, ...cur.orders.map((o) => o.classe)], []);

  const uniqueClasses = [...new Set(rawClasses.map((c) => c.id))]
    .map((id) => rawClasses.find((c) => c.id == id))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));

  return uniqueClasses.map((c) => ({
    ...c,
    id: c.id,
    name: `${c.name} - ${
      c.teachers.map((t) => t.name).join(", ") || "no teacher"
    }`,
    orderedIngredients: orderedIngredients
      .map((oi) => ({
        ...oi,
        orders: oi.orders.filter((o) => o.classe.id == c.id),
      }))
      .filter((oi) => oi.orders.length),
  }));
};

export const getRoomsFromOrderedIngredients: (
  orderedIngredients: ClassViewGridQuery["orderedIngredients"]
) => GroupedOrderedIngredients = (orderedIngredients) => {
  const rawRooms = orderedIngredients.reduce<
    ClassViewGridQuery["orderedIngredients"][number]["orders"][number]["classe"]["metaRoom"][]
  >((acc, cur) => [...acc, ...cur.orders.map((o) => o.classe.metaRoom)], []);

  const uniqueRooms = [...new Set(rawRooms.map((r) => r || undefined))];

  return uniqueRooms.map<GroupedOrderedIngredients[number]>((room) => ({
    id: room || "",
    name: room || "No Room",
    orderedIngredients: orderedIngredients
      .map((oi) => ({
        ...oi,
        orders: oi.orders.filter((o) =>
          room ? o.classe.metaRoom === room : !o.classe.metaRoom
        ),
      }))
      .filter((oi) => oi.orders.length),
  }));
};

export const getUsersFromOrderedIngredients: (
  orderedIngredients: ClassViewGridQuery["orderedIngredients"]
) => GroupedOrderedIngredients = (orderedIngredients) => {
  const rawUsers = orderedIngredients.reduce<
    ClassViewGridQuery["orderedIngredients"][number]["orders"][number]["user"][]
  >((acc, cur) => [...acc, ...cur.orders.map((o) => o.user)], []);

  const uniqueUsers = [...new Set(rawUsers.map((u) => u.id))]
    .map((id) => rawUsers.find((u) => u.id == id))
    .filter((u): u is NonNullable<typeof u> => Boolean(u));

  return uniqueUsers.map<GroupedOrderedIngredients[number]>((u) => ({
    id: u.id,
    name: u.name,
    orderedIngredients: orderedIngredients
      .map((oi) => ({
        ...oi,
        orders: oi.orders.filter((o) => o.user.id == u.id),
      }))
      .filter((oi) => oi.orders.length),
  }));
};

export const getRecipesFromOrderedIngredients: (
  orderedIngredients: ClassViewGridQuery["orderedIngredients"]
) => GroupedOrderedIngredients = (orderedIngredients) => {
  const rawRecipes = orderedIngredients.reduce<
    ClassViewGridQuery["orderedIngredients"][number]["orders"][number]["orderSession"]["recipe"][]
  >(
    (acc, cur) => [
      ...acc,
      ...cur.orders
        .filter((o) => o.orderSession.recipe && o.orderSession.servings)
        .map((o) => o.orderSession.recipe),
    ],
    []
  );

  const uniqueRecipes = [...new Set(rawRecipes.map((r) => r?.id))]
    .map((id) => rawRecipes.find((r) => r?.id == id))
    .filter((recipe): recipe is NonNullable<typeof recipe> => Boolean(recipe));

  return uniqueRecipes
    .map<GroupedOrderedIngredients[number]>((r) => ({
      id: r.id,
      name: r.name,
      orderedIngredients: orderedIngredients
        .map((oi) => ({
          ...oi,
          orders: oi.orders
            .filter((o) => o.orderSession.recipe && o.orderSession.servings)
            .filter((o) => o.orderSession.recipe?.id == r.id),
        }))
        .filter((oi) => oi.orders.length),
    }))
    .concat([
      {
        id: "empty",
        name: "No Recipe",
        orderedIngredients: orderedIngredients
          .map((oi) => ({
            ...oi,
            orders: oi.orders.filter(
              (o) => !(o.orderSession.recipe && o.orderSession.servings)
            ),
          }))
          .filter((oi) => oi.orders.length),
      },
    ]);
};

// Order By Type
export type OrderBy =
  | "name_ASC"
  | "name_DESC"
  | "createdAt_ASC"
  | "createdAt_DESC"
  | "updatedAt_ASC"
  | "updatedAt_DESC"
  | "tag";

export const groupBy: <T>(arr: T[], key: string) => { [name: string]: T[] } = (
  arr,
  key
) =>
  arr.reduce(
    (acc, item) => ((acc[item[key]] = [...(acc[item[key]] || []), item]), acc),
    {}
  );

export function groupByFn<T>(arr: T[], fn: (item: T) => string | number) {
  return arr.reduce<{ [name: string]: T[] }>(
    (acc, item) => ((acc[fn(item)] = [...(acc[fn(item)] || []), item]), acc),
    {}
  );
}

// Example
// groupBy([
//     { branch: 'audi', model: 'q8', year: '2019' },
//     { branch: 'audi', model: 'rs7', year: '2020' },
//     { branch: 'ford', model: 'mustang', year: '2019' },
//     { branch: 'ford', model: 'explorer', year: '2020' },
//     { branch: 'bmw', model: 'x7', year: '2020' },
// ], 'branch');

/*
{
    audi: [
        { branch: 'audi', model: 'q8', year: '2019' },
        { branch: 'audi', model: 'rs7', year: '2020' }
    ],
    bmw: [
        { branch: 'bmw', model: 'x7', year: '2020' }
    ],
    ford: [
        { branch: 'ford', model: 'mustang', year: '2019' },
        { branch: 'ford', model: 'explorer', year: '2020' }
    ],
}
*/

// A function to copy text to clipboard
export const updateClipboard: (
  newClip: string,
  onCompleted?: () => void
) => void = async (newClip, onCompleted) => {
  try {
    await navigator.clipboard.writeText(newClip);
    if (onCompleted) {
      onCompleted();
    }
  } catch (error) {
    console.error(error);
  }
};

export function getName(name: string) {
  const [first, ...rest] = name.split(" ");
  const last = rest[rest.length - 1];
  switch (first) {
    case "Mrs":
    case "Mrs.":
    case "Ms":
    case "Ms.":
      return `${first} ${last}`;
      break;

    default:
      return first;
      break;
  }
}

export function getFirstName(name: string) {
  const [first, ...rest] = name.split(" ");
  const [second, ...extra] = rest;
  switch (first) {
    case "Mrs":
    case "Mrs.":
    case "Ms":
    case "Ms.":
    case "Miss":
    case "Mr":
    case "Mr.":
      return second;
      break;

    default:
      return first;
      break;
  }
}

export const roleKeys = {
  student: "ck96vqt22032t0793snorqjsm",
  teacher: "ck96vr0u303320793vp53fjp6",
  admin: "ck96vr7eo033b0793osivgm9p",
  assistant: "ck96vrdiv033k0793uzst2qlt",
};

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const whiteListedSchools = [
  //  "Example School",
  "Central College",
  "Example School/College",
  //  "ClassCart School",
  "Holroyd High School",
  "Endeavour College",
];

export const coupons = [
  { name: "Endeavour College", coupon: "1kKN74sH" },
  { name: "Holroyd High School", coupon: "1kKN74sH" },
  { name: "Seaview High School", coupon: "1kKN74sH" },
  { name: "Wangaratta High School", coupon: "1kKN74sH" },
];

// Zero Indexed Months
const JANUARY = 0;
const FEBRUARY = 1;
const MARCH = 2;
const APRIL = 3;
const MAY = 4;
const JUNE = 5;
const JULY = 6;
const AUGUST = 7;
const SEPTEMBER = 8;
const OCTOBER = 9;
const NOVEMBER = 10;
const DECEMBER = 11;

export const CURRENT_YEAR = 2026;

export const termDates = [
  {
    state: "ACT",
    years: [
      {
        year: 2026,
        terms: [
          {
            name: "Term 1 - 2026",
            start: new Date(2026, JANUARY, 30),
            end: new Date(2026, APRIL, 2),
          },
          {
            name: "Term 2 - 2026",
            start: new Date(2026, APRIL, 21),
            end: new Date(2026, JULY, 3),
          },
          {
            name: "Term 3 - 2026",
            start: new Date(2026, JULY, 21),
            end: new Date(2026, SEPTEMBER, 25),
          },
          {
            name: "Term 4 - 2026",
            start: new Date(2026, OCTOBER, 13),
            end: new Date(2026, DECEMBER, 18),
          },
        ],
      },
    ],
  },
  {
    state: "NSW",
    years: [
      {
        year: 2026,
        terms: [
          {
            name: "Term 1 - 2026",
            start: new Date(2026, FEBRUARY, 2),
            end: new Date(2026, APRIL, 2),
          },
          {
            name: "Term 2 - 2026",
            start: new Date(2026, APRIL, 22),
            end: new Date(2026, JULY, 3),
          },
          {
            name: "Term 3 - 2026",
            start: new Date(2026, JULY, 21),
            end: new Date(2026, SEPTEMBER, 25),
          },
          {
            name: "Term 4 - 2026",
            start: new Date(2026, OCTOBER, 13),
            end: new Date(2026, DECEMBER, 17),
          },
        ],
      },
    ],
  },
  {
    state: "NT",
    years: [
      {
        year: 2026,
        terms: [
          {
            name: "Term 1 - 2026",
            start: new Date(2026, JANUARY, 29),
            end: new Date(2026, APRIL, 2),
          },
          {
            name: "Term 2 - 2026",
            start: new Date(2026, APRIL, 14),
            end: new Date(2026, JUNE, 19),
          },
          {
            name: "Term 3 - 2026",
            start: new Date(2026, JULY, 14),
            end: new Date(2026, SEPTEMBER, 18),
          },
          {
            name: "Term 4 - 2026",
            start: new Date(2026, OCTOBER, 6),
            end: new Date(2026, DECEMBER, 10),
          },
        ],
      },
    ],
  },
  {
    state: "QLD",
    years: [
      {
        year: 2026,
        terms: [
          {
            name: "Term 1 - 2026",
            start: new Date(2026, JANUARY, 27),
            end: new Date(2026, APRIL, 2),
          },
          {
            name: "Term 2 - 2026",
            start: new Date(2026, APRIL, 20),
            end: new Date(2026, JUNE, 26),
          },
          {
            name: "Term 3 - 2026",
            start: new Date(2026, JULY, 13),
            end: new Date(2026, SEPTEMBER, 18),
          },
          {
            name: "Term 4 - 2026",
            start: new Date(2026, OCTOBER, 6),
            end: new Date(2026, DECEMBER, 11),
          },
        ],
      },
    ],
  },
  {
    state: "SA",
    years: [
      {
        year: 2026,
        terms: [
          {
            name: "Term 1 - 2026",
            start: new Date(2026, JANUARY, 27),
            end: new Date(2026, APRIL, 10),
          },
          {
            name: "Term 2 - 2026",
            start: new Date(2026, APRIL, 27),
            end: new Date(2026, JULY, 3),
          },
          {
            name: "Term 3 - 2026",
            start: new Date(2026, JULY, 20),
            end: new Date(2026, SEPTEMBER, 25),
          },
          {
            name: "Term 4 - 2026",
            start: new Date(2026, OCTOBER, 12),
            end: new Date(2026, DECEMBER, 11),
          },
        ],
      },
    ],
  },
  {
    state: "Tas",
    years: [
      {
        year: 2026,
        terms: [
          {
            name: "Term 1 - 2026",
            start: new Date(2026, FEBRUARY, 5),
            end: new Date(2026, APRIL, 17),
          },
          {
            name: "Term 2 - 2026",
            start: new Date(2026, MAY, 4),
            end: new Date(2026, JULY, 10),
          },
          {
            name: "Term 3 - 2026",
            start: new Date(2026, JULY, 27),
            end: new Date(2026, OCTOBER, 2),
          },
          {
            name: "Term 4 - 2026",
            start: new Date(2026, OCTOBER, 19),
            end: new Date(2026, DECEMBER, 18),
          },
        ],
      },
    ],
  },
  {
    state: "Vic",
    years: [
      {
        year: 2026,
        terms: [
          {
            name: "Term 1 - 2026",
            start: new Date(2026, JANUARY, 27),
            end: new Date(2026, APRIL, 2),
          },
          {
            name: "Term 2 - 2026",
            start: new Date(2026, APRIL, 20),
            end: new Date(2026, JUNE, 26),
          },
          {
            name: "Term 3 - 2026",
            start: new Date(2026, JULY, 13),
            end: new Date(2026, SEPTEMBER, 18),
          },
          {
            name: "Term 4 - 2026",
            start: new Date(2026, OCTOBER, 5),
            end: new Date(2026, DECEMBER, 18),
          },
        ],
      },
    ],
  },
  {
    state: "WA",
    years: [
      {
        year: 2026,
        terms: [
          {
            name: "Term 1 - 2026",
            start: new Date(2026, FEBRUARY, 2),
            end: new Date(2026, APRIL, 2),
          },
          {
            name: "Term 2 - 2026",
            start: new Date(2026, APRIL, 20),
            end: new Date(2026, JULY, 3),
          },
          {
            name: "Term 3 - 2026",
            start: new Date(2026, JULY, 20),
            end: new Date(2026, SEPTEMBER, 25),
          },
          {
            name: "Term 4 - 2026",
            start: new Date(2026, OCTOBER, 12),
            end: new Date(2026, DECEMBER, 17),
          },
        ],
      },
    ],
  },
  {
    state: "NZ",
    years: [
      {
        year: 2026,
        terms: [
          {
            name: "Term 1 - 2026",
            start: new Date(2026, JANUARY, 26),
            end: new Date(2026, APRIL, 2),
          },
          {
            name: "Term 2 - 2026",
            start: new Date(2026, APRIL, 20),
            end: new Date(2026, JULY, 3),
          },
          {
            name: "Term 3 - 2026",
            start: new Date(2026, JULY, 20),
            end: new Date(2026, SEPTEMBER, 25),
          },
          {
            name: "Term 4 - 2026",
            start: new Date(2026, OCTOBER, 12),
            end: new Date(2026, DECEMBER, 18),
          },
        ],
      },
    ],
  },
];

export function fillArray<Type>(length: number, value: Type): Type[] {
  return Array.from(Array(length)).map(() => value);
}

export function getIngredientMeasurementDisplay(
  ingredient: Pick<
    NonNullable<IngredientQuery["ingredient"]>,
    "name" | "measurement" | "units" | "density"
  >,
  iconClass: HTMLAttributes<HTMLElement>["className"] = ""
) {
  return (
    <>
      {ingredient.measurement?.name == "Weight" ? (
        ingredient.density ? (
          <>
            <CubeIcon
              className={classNames(
                "icon mx-0 text-amber-600 opacity-50 transition hover:scale-100 group-hover:opacity-100",
                iconClass
              )}
            />
            <BeakerIcon
              className={classNames(
                "icon mx-0 text-amber-600 opacity-50 transition hover:scale-100 group-hover:opacity-100",
                iconClass
              )}
            />
            <small className="text-amber-600">{ingredient.density}</small>
          </>
        ) : (
          <CubeIcon
            className={classNames(
              "icon mx-0 opacity-50 transition hover:scale-100 group-hover:opacity-100",
              iconClass
            )}
          />
        )
      ) : null}
      {ingredient.measurement?.name == "Volume" ? (
        <BeakerIcon
          className={classNames(
            "icon mx-0 opacity-50 transition hover:scale-100 group-hover:opacity-100",
            iconClass
          )}
        />
      ) : null}
      {ingredient.units.length ? (
        <RectangleStackIcon
          className={classNames(
            "icon mx-0 opacity-50 transition hover:scale-100 group-hover:opacity-100",
            iconClass
          )}
        />
      ) : null}
    </>
  );
}

export function getIngredientMeasurementDescription(
  ingredient: Pick<
    NonNullable<IngredientQuery["ingredient"]>,
    "name" | "measurement" | "units" | "density"
  >
) {
  return (
    <p className="font-normal">
      {ingredient.name} is measured{" "}
      {ingredient.measurement ? (
        <span>
          in{" "}
          {ingredient.measurement?.name == "Weight" && ingredient.density
            ? "both"
            : null}{" "}
          {ingredient.measurement?.name == "Weight" ? "weight" : null}{" "}
          {ingredient.measurement?.name == "Weight" && ingredient.density
            ? "and volume"
            : null}{" "}
          {ingredient.measurement?.name == "Volume" ? "volume" : null}
          {" units"}
          {ingredient.measurement?.name == "Weight" && ingredient.density ? (
            <span>
              {" "}
              (with a density of {ingredient.density} kg/m
              <sup>3</sup> for conversions)
            </span>
          ) : null}
        </span>
      ) : (
        "with "
      )}
      {ingredient.measurement && ingredient.units.length
        ? " as well as "
        : null}
      {ingredient.units.length ? (
        <span>
          {ingredient.units.length} discrete unit
          {ingredient.units.length > 1 ? "s" : null}:{" "}
          {ingredient.units.map((u) => u.name).join(", ")}
        </span>
      ) : null}
      .
    </p>
  );
}

export async function searchIngredientLink(
  url: string,
  name: string,
  units: string[],
  controller?: AbortController
) {
  let signal: AbortSignal | undefined;

  if (controller) signal = controller.signal;

  const data: CostToUnitData = await fetch(
    `/api/gpt3-cost-to-unit?url=${encodeURIComponent(
      url
    )}&name=${encodeURIComponent(name)}${units
      .map((unit) => `&units=${encodeURIComponent(unit)}`)
      .join("")}`,
    {
      signal,
    }
  );

  return data;
}

export async function searchManyIngredientLinks(
  url: string,
  name: string,
  units: string[],
  controller?: AbortController
) {
  let signal: AbortSignal | undefined;

  if (controller) signal = controller.signal;

  const data: IngredientSearchData = await fetch(
    `/api/gpt3-ingredient-search?url=${encodeURIComponent(
      url
    )}&name=${encodeURIComponent(name)}${units
      .map((unit) => `&units=${encodeURIComponent(unit)}`)
      .join("")}`,
    {
      signal,
    }
  );

  return data;
}

export async function ingredientNameToQuery(
  name: string,
  controller?: AbortController
) {
  let signal: AbortSignal | undefined;

  if (controller) signal = controller.signal;

  const data: { result: string } = await fetch(
    `/api/gpt3-ingredient-name-to-query?ingredient=${encodeURIComponent(name)}`,
    {
      signal,
    }
  );

  return data;
}

export async function wait(ms: number) {
  return new Promise<boolean>((resolve) => {
    setTimeout(() => resolve(true), ms);
  });
}

export function capitalise(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function canCreateIngredients(user: CurrentUser) {
  return user
    ? !(isStudent(user) && !user.school?.permCreateIngredients)
    : false;
}

export const cantFreeFormOrder = (user: CurrentUser) =>
  isStudent(user) && !user.school?.permOrderOnCalendar;

export function shuffle<T>(array: T[]): T[] {
  return array.sort(() => Math.random() - 0.5);
}
